import { useEffect, useRef, useState } from "react";

/**
 * Renders a looping muted video that only plays while in the viewport.
 * Falls back gracefully to the poster image when `video` is not provided,
 * so any consumer can pass either a video URL or just an image.
 */
export const AmbientMedia = ({
  video,
  poster,
  alt = "",
  className = "",
  eager = false,
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !video) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisible(entry.isIntersecting);
          if (entry.isIntersecting) {
            el.play().catch(() => {});
          } else {
            el.pause();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [video]);

  if (!video) {
    return <img src={poster} alt={alt} className={className} {...rest} />;
  }

  return (
    <video
      ref={ref}
      src={video}
      poster={poster}
      muted
      loop
      playsInline
      preload={eager ? "auto" : "metadata"}
      aria-label={alt}
      className={className}
      data-visible={visible ? "true" : "false"}
      {...rest}
    />
  );
};
